import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { serviceUris } from '../../../../environments/services';
import { inject, Injectable } from '@angular/core';
import { HubLogFilter } from '../models/hubLog-filter.model';
import { PaginatedHubLogResponse } from '../models/paginated-hub-log.model';
import { PaginatedHubEventLogResponse } from '../models/paginated-hub-event-log.model';

@Injectable({
  providedIn: 'root'
})
export class HubLogService {
  private readonly http = inject(HttpClient);

  /**
   * Search hub logs based on provided filters and pagination parameters.
   * @param filters - An object containing the filter criteria for searching hub logs.
   * @param pageNumber - The page number for pagination.
   * @param pageSize - The number of items to be displayed per page.
   */
  searchHubLog(filters: HubLogFilter, pageNumber: number, pageSize: number): Observable<PaginatedHubLogResponse> {
    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };
    const url = `${environment.backOfficeUrl}${serviceUris.searchHubLogList}`;
    return this.http.get<PaginatedHubLogResponse>(url, { params: params });
  }

  /**
   * Search hub event logs based on the provided native ID and pagination parameters.
   * @param nativeId - The native ID to filter the hub event logs.
   * @param pageNumber - The page number for pagination.
   * @param pageSize - The number of items to be displayed per page.
   * @returns An observable containing the paginated hub event log response.
   */
  searchHubEventLog(nativeId: string, pageNumber: number, pageSize: number): Observable<PaginatedHubEventLogResponse> {
    const params: any = {
      nativeId,
      page: pageNumber,
      pageSize
    };
    const url = `${environment.backOfficeUrl}${serviceUris.searchHubEventLogList}`;
    return this.http.get<PaginatedHubEventLogResponse>(url, { params: params });
  }

  /**
   * Delete a hub log record by nativeId.
   * @param nativeId - The native ID of the hub log to delete.
   */
  deleteHubLog(nativeId: string): Observable<any> {
    const params: any = { nativeId };
    const url = `${environment.backOfficeUrl}${serviceUris.deleteHubLog}`;
    return this.http.delete<any>(url, { params: params });
  }

  /**
   * Clear hub logs.
   */
  clearHubLogs(): Observable<any> {
    const url = `${environment.backOfficeUrl}${serviceUris.clearHubLogs}`;
    return this.http.delete<any>(url);
  }

  /**
   * Fetch hub process options for the logs search dropdown.
   */
  getHubProcessList(): Observable<string[]> {
    const url = `${environment.backOfficeUrl}${serviceUris.getHubProcessList}`;
    return this.http.get<any>(url).pipe(
      map((response) => {
        const raw = response?.data ?? response;

        if (Array.isArray(raw)) {
          return raw.map((item) => String(item).trim()).filter((item) => !!item);
        }
        return [];
      })
    );
  }
}
